import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { SkeletonCard } from "@/components/Skeletons";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import client, { apiError } from "@/lib/api";
import { Check, Plus, AlertTriangle, HelpCircle, Lock, ExternalLink } from "lucide-react";

const STATE = {
    encaja: { I: Check, c: "text-ok", b: "border-ok/30 bg-ok/10", t: "Encaja" },
    complementa: { I: Plus, c: "text-gold", b: "border-gold/30 bg-gold/10", t: "Complementa" },
    revisar: { I: AlertTriangle, c: "text-al", b: "border-al/30 bg-al/10", t: "Merece revisión" },
    insuficiente: { I: HelpCircle, c: "text-subtle", b: "border-line bg-arena", t: "Información insuficiente" },
};
const ACTION = {
    MANTENER: "Mantener", AJUSTAR: "Ajustar", NO_RECOMENDAR_CONTINUAR_ASI: "No continuar así",
    PENDIENTE_DE_INFORMACION: "Pendiente de información", TERMINAR_Y_SUSTITUIR: "Terminar y sustituir",
};

function MirrorTable({ rows }) {
    return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {rows.map((r) => {
                const s = STATE[r.state] || STATE.insuficiente;
                return (
                    <div key={r.product_id} className="flex items-start gap-3 rounded-lg border border-line bg-card p-4"
                        data-testid={`mirror-row-${r.product_id}`}>
                        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${s.b} ${s.c}`}>
                            <s.I size={16} />
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <p className="text-[15px] font-medium text-ink">{r.label}</p>
                                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${s.b} ${s.c}`}>{s.t}</span>
                            </div>
                            {r.function && (
                                <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-subtle" data-testid={`mirror-fn-${r.product_id}`}>{r.function}</p>
                            )}
                            <p className="mt-1 text-[13px] leading-[1.5] text-subtle">{r.reason}</p>
                            {r.learning && (
                                <p className="mt-2 border-l-2 border-terracotta/40 pl-3 text-[12px] italic leading-[1.55] text-subtle/90" data-testid={`mirror-learning-${r.product_id}`}>{r.learning}</p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function Resultado() {
    const { settings } = useAuth();
    const [mirror, setMirror] = useState(undefined);
    const [report, setReport] = useState(null);
    const [paying, setPaying] = useState(false);

    const load = async () => {
        try {
            const { data } = await client.get("/criteri/mirror");
            setMirror(data);
            if (data.paid) {
                const r = await client.get("/criteri/report");
                setReport(r.data);
            }
        } catch (e) {
            setMirror(null);
        }
    };
    useEffect(() => { load(); }, []);

    const pay = async () => {
        setPaying(true);
        try {
            const { data } = await client.post("/payments/checkout", { origin_url: window.location.origin });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(apiError(e));
            setPaying(false);
        }
    };

    if (mirror === undefined) return (<><PageHeader title="Tu resultado" /><SkeletonCard /></>);
    if (mirror === null)
        return (
            <>
                <PageHeader title="Tu resultado" />
                <EmptyState testId="resultado-empty" title="Aún no hay nada que cruzar"
                    text="Completa tu test y añade lo que tienes en el neceser. Después te mostramos tu Tabla Espejo."
                    action={<Button asChild><Link to="/app/analisis/nuevo">Empezar mi test</Link></Button>} />
            </>
        );

    return (
        <>
            <PageHeader title="Tu Tabla Espejo"
                subtitle="Esto es lo que vemos al cruzar lo que nos has contado con lo que ya tienes." />

            <MirrorTable rows={mirror.mirror} />

            <div className="mt-6 rounded-lg border border-line bg-card p-5">
                <h3 className="text-[15px] font-bold text-ink">Cobertura de tus funciones base</h3>
                <div className="mt-3 space-y-3">
                    {(mirror.coverage_detail || []).map((c) => (
                        <div key={c.function} className="text-[13px] leading-[1.5]" data-testid={`coverage-${c.function}`}>
                            <div className="flex flex-wrap items-baseline gap-x-2">
                                <span className="font-medium uppercase tracking-wide text-ink">{c.label}</span>
                                <span className="font-semibold uppercase tracking-wide"
                                    style={{ color: c.covered ? "#5C7A62" : "#B3291E" }}>
                                    {c.covered ? "Cubierta" : "Sin cubrir"}
                                </span>
                            </div>
                            {c.covered && c.products.length > 0 && (
                                <p className="mt-0.5 text-subtle">{c.products.join(", ")}</p>
                            )}
                            {!c.covered && c.reason && (
                                <p className="mt-0.5 text-subtle">{c.reason}</p>
                            )}
                        </div>
                    ))}
                </div>
                {mirror.has_treatment && (
                    <p className="mt-3 text-[13px] leading-[1.5] text-subtle" data-testid="treatment-note">
                        Además de las funciones básicas, tu neceser incorpora activos de tratamiento.
                    </p>
                )}
                <div className="mt-4">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/criteri/mirror.pdf`} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" data-testid="mirror-pdf">Descargar mi resumen en PDF</Button>
                    </a>
                </div>
            </div>

            {mirror.closing && (
                <div className="mt-4 rounded-lg border border-line bg-background p-5" data-testid="mirror-closing">
                    <p className="text-[14px] leading-[1.6] text-ink">{mirror.closing}</p>
                </div>
            )}

            {!mirror.paid && mirror.show_bridge && (
                <div className="mt-4 rounded-lg border border-gold/40 bg-gold/5 p-5" data-testid="mirror-bridge">
                    <p className="text-[14px] leading-[1.6] text-ink">{mirror.bridge_text}</p>
                    <div className="mt-4">
                        <Button onClick={pay} disabled={paying} data-testid="bridge-unlock-button">
                            {paying ? "Abriendo pago…" : `Ver mi análisis completo · ${settings?.plan_premium_price || "19,90 €"}`}
                        </Button>
                    </div>
                </div>
            )}

            {!mirror.paid ? (
                <div className="mt-8 rounded-lg border border-line bg-ink p-6 text-cream" data-testid="paywall">
                    <div className="flex items-center gap-2"><Lock size={18} className="text-terracotta" /><h3 className="font-display text-[22px] font-bold">Desbloquea tu análisis completo</h3></div>
                    <ul className="mt-3 grid gap-1.5 text-[14px] leading-[1.5] text-cream/80 sm:grid-cols-2">
                        <li>· Análisis producto a producto</li>
                        <li>· Qué mantener, ajustar, revisar o dejar</li>
                        <li>· Rutina actual</li>
                        <li>· Plan de transición</li>
                        <li>· Rutina objetivo</li>
                        <li>· Rutina de mañana y noche</li>
                        <li>· Orden, frecuencia y alternancias</li>
                        <li>· Carencias reales</li>
                        <li>· Recomendaciones concretas solo si hacen falta</li>
                        <li>· Informe completo en PDF</li>
                    </ul>
                    <div className="mt-5 flex items-center gap-4">
                        <span className="tnum text-[26px] font-bold">{settings?.plan_premium_price || "19,90 €"}</span>
                        <span className="text-[13px] text-cream/60">· pago único</span>
                        <Button onClick={pay} disabled={paying} data-testid="unlock-button">
                            {paying ? "Abriendo pago…" : "Desbloquear mi análisis completo"}
                        </Button>
                    </div>
                    <p className="mt-3 text-[12px] text-cream/50">Pago único y seguro con tarjeta.</p>
                </div>
            ) : report ? (
                <div className="mt-8 space-y-8" data-testid="full-report">
                    <div className="flex items-center justify-between">
                        <p className="text-[13px] text-subtle">Informe desbloqueado · revisión {report.revision}</p>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/criteri/report.pdf`} target="_blank" rel="noreferrer">
                            <Button variant="outline" size="sm" data-testid="report-pdf">Descargar PDF</Button>
                        </a>
                    </div>
                    <section>
                        <h2 className="font-display text-[24px] font-bold text-ink">Tu rutina ahora y plan de transición</h2>
                        <div className="mt-4 overflow-hidden rounded-lg border border-line bg-card">
                            {report.transition_plan.map((t, i) => (
                                <div key={i} className="flex items-center justify-between gap-3 border-b border-line p-4 last:border-0">
                                    <span className="text-[15px] text-ink">{t.product}</span>
                                    <span className="rounded-full border border-line bg-background px-2.5 py-0.5 text-[12px] font-medium text-subtle">{ACTION[t.action] || t.action}</span>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <h2 className="font-display text-[24px] font-bold text-ink">Carencias reales</h2>
                        {report.gaps.length === 0 ? (
                            <p className="mt-2 text-[15px] text-subtle">No hemos identificado carencias. Con lo que tienes, no hay una compra que podamos justificar ahora.</p>
                        ) : (
                            <div className="mt-4 space-y-4">
                                {report.recommendations.map((r) => (
                                    <div key={r.function} className="rounded-lg border border-line bg-card p-5" data-testid={`gap-${r.function}`}>
                                        <p className="text-[16px] font-bold text-ink">{r.label}</p>
                                        {r.status === "CATALOG_NO_MATCH" ? (
                                            <p className="mt-2 text-[14px] leading-[1.6] text-subtle">{r.note}</p>
                                        ) : (
                                            <div className="mt-4 grid gap-3 sm:grid-cols-3">
                                                {["low", "optima", "premium"].filter((t) => r.options[t]).map((t) => {
                                                    const o = r.options[t];
                                                    const label = { low: "Low", optima: "Óptima", premium: "Premium" }[t];
                                                    return (
                                                        <a key={t} href={o.link} target="_blank" rel="noreferrer"
                                                            className="group rounded-lg border border-line bg-background p-4 transition-colors duration-150 hover:border-terracotta/50">
                                                            <p className="text-[12px] font-medium uppercase tracking-wide text-subtle">{label}</p>
                                                            <p className="mt-1.5 text-[14px] font-medium text-ink">{o.brand}</p>
                                                            <p className="text-[13px] text-subtle">{o.name}</p>
                                                            <p className="mt-2 text-[12px] text-subtle">{o.price_label}</p>
                                                            <p className="mt-1 text-[11px] text-subtle">Fuente: {o.source}</p>
                                                            <ExternalLink size={13} className="mt-2 text-subtle group-hover:text-terracotta" />
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="rounded-lg border border-line bg-background p-5">
                        <h3 className="text-[14px] font-bold text-ink">Límites y fuentes</h3>
                        <ul className="mt-2 space-y-1 text-[13px] leading-[1.6] text-subtle">
                            {report.limits.map((l, i) => <li key={i}>· {l}</li>)}
                        </ul>
                        <p className="mt-3 text-[13px] text-subtle">
                            Reglas aplicadas: {report.rules_applied.map((r) => r.id).join(", ")}. La decisión es siempre tuya.
                        </p>
                    </section>
                </div>
            ) : <SkeletonCard />}
        </>
    );
}
